import { OCR_STATUS } from "@/constants/caseFileOcrStatus";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { OcrTextBodyView } from "./OcrTextBodyView";
import { type OcrTextBodyProps } from "./types";
import { parseOcrPages } from "./utils";

export function OcrTextBody({ status, currentPage, onOcrPageChange }: OcrTextBodyProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef(new Map<number, HTMLDivElement>());
  const isAutoScrolling = useRef(false);
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;

  const sections = useMemo(() => {
    if (status.kind !== OCR_STATUS.SUCCESS) return [];
    return parseOcrPages(status.text);
  }, [status]);

  const setSectionRef = useCallback((pageNumber: number, el: HTMLDivElement | null) => {
    if (el) {
      sectionRefs.current.set(pageNumber, el);
    } else {
      sectionRefs.current.delete(pageNumber);
    }
  }, []);

  useEffect(() => {
    if (sections.length === 0 || !containerRef.current) return;

    const targetPage = (currentPageRef.current ?? 0) + 1;
    const targetEl = sectionRefs.current.get(targetPage);
    if (!targetEl) return;

    const container = containerRef.current;
    isAutoScrolling.current = true;
    container.scrollTop =
      targetEl.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop;

    requestAnimationFrame(() => {
      isAutoScrolling.current = false;
    });
  }, [sections]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !onOcrPageChange) return;

    const handleScroll = () => {
      if (isAutoScrolling.current) return;

      const containerTop = container.getBoundingClientRect().top;
      let visiblePage = sections[0]?.pageNumber ?? 1;

      const sorted = [...sectionRefs.current.entries()].sort(([a], [b]) => a - b);
      for (const [pageNumber, el] of sorted) {
        if (el.getBoundingClientRect().top <= containerTop + 50) {
          visiblePage = pageNumber;
        }
      }

      onOcrPageChange(visiblePage - 1);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [onOcrPageChange, sections]);

  return (
    <OcrTextBodyView
      status={status}
      sections={sections}
      containerRef={containerRef}
      setSectionRef={setSectionRef}
    />
  );
}
